import { OpenFeature } from '@openfeature/web-sdk';
import { ConfigCatWebProvider } from '@openfeature/config-cat-web-provider';
import { useEffect, useMemo, useRef, useState } from 'react';

let openFeatureClient = null;
let openFeatureCommonClient = null;
let isInitialized = false;
let isCommonInitialized = false;
// Promise-based locks: concurrent callers await the same in-flight promise rather than
// bailing out early (which would return defaultValue while init is still running).
let _initPromise = null;
let _commonInitPromise = null;

// Simple subscription system to re-render hooks when flags change
const subscribers = new Set();
function subscribe(cb) {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

// Debounced subscriber notification — collapses rapid configChanged bursts into
// a single pass and breaks any synchronous configChanged → evaluate → configChanged cycle.
let _notifyTimer = null;
function notifySubscribers() {
  clearTimeout(_notifyTimer);
  _notifyTimer = setTimeout(() => {
    subscribers.forEach((cb) => {
      try { cb(); } catch (e) { /* noop */ }
    });
  }, 0);
}

// initalise the flag overrides from the url (this will run on every page load)
export function initializeFlagOverridesFromURL() {
  if (typeof window === 'undefined') return;
  
  const urlParams = new URLSearchParams(window.location.search);
  
  // optional clear command: ?flags=clear
  if (urlParams.get('flags') === 'clear') {
    localStorage.removeItem('__FLAG_URL_OVERRIDES');
    return;
  }
  
  // store flags with current timestamp
  const storeFlags = (flags) => {
    const data = {
      flags,
      timestamp: Date.now()
    };
    localStorage.setItem('__FLAG_URL_OVERRIDES', JSON.stringify(data));
  };
  
  let flagsStored = false;
  
  // multiple flags: ?flags={"flag1":true,"flag2":false}
  if (urlParams.has('flags')) {
    try {
      const urlFlags = JSON.parse(urlParams.get('flags'));
      storeFlags(urlFlags);
      flagsStored = true;
    } catch (e) {
      // invalid json so skip
    }
  }
  
  // single flags: ?flag1=true&flag2=false
  if (!flagsStored) {
    const singleFlags = {};
    for (const [key, value] of urlParams.entries()) {
      if (value === 'true' || value === 'false') {
        singleFlags[key] = value === 'true';
      }
    }
    if (Object.keys(singleFlags).length > 0) {
      try {
        const stored = localStorage.getItem('__FLAG_URL_OVERRIDES');
        const data = stored ? JSON.parse(stored) : { flags: {}, timestamp: Date.now() };
        data.flags = { ...data.flags, ...singleFlags };
        data.timestamp = Date.now();
        localStorage.setItem('__FLAG_URL_OVERRIDES', JSON.stringify(data));
      } catch (e) {
        storeFlags(singleFlags);
      }
    }
  }
}

// Set clientAlias and topicAlias in the OpenFeature global context.
// The OpenFeature *web* SDK is context-driven: context must be set globally via
// setContext() — per-call context passed to getBooleanValue() is treated as options
// (hooks only) and is not forwarded to the provider.
// Call this on initial load and on every Gatsby route change.
export async function setFlagRouteContext(pathname) {
  if (typeof window === 'undefined') return;
  const p = pathname != null ? pathname : window.location.pathname;
  const segments = p.split('/').filter(Boolean);
  const ctx = {};
  if (segments[0]) ctx.clientAlias = segments[0];
  if (segments[1]) ctx.topicAlias = segments[1];
  // Always call setContext — even with an empty object — so that navigating away
  // from an aliased route clears stale clientAlias/topicAlias values.
  await OpenFeature.setContext(ctx);
}

// Override helpers (browser or test)
function getOverride(key) {
  // Browser-based override (e2e / manual)
  if (typeof window !== 'undefined' && window.__FLAG_OVERRIDES && Object.prototype.hasOwnProperty.call(window.__FLAG_OVERRIDES, key)) {
    return window.__FLAG_OVERRIDES[key];
  }
  
  // Check localStorage for persisted URL overrides (with expiry check)
  if (typeof window !== 'undefined') {
    const expiryTime = 60 * 60 * 1000; 
    
    try {
      const stored = localStorage.getItem('__FLAG_URL_OVERRIDES');
      if (stored) {
        const data = JSON.parse(stored);
        const age = Date.now() - (data.timestamp || 0);
        
        // Check if expired (older than 1 hour)
        if (age > expiryTime) {
          localStorage.removeItem('__FLAG_URL_OVERRIDES');
          return undefined;
        }
        
        // Not expired, return the flag value
        if (data.flags && Object.prototype.hasOwnProperty.call(data.flags, key)) {
          return data.flags[key];
        }
      }
    } catch (e) {
      // Invalid localStorage data - ignore and continue
    }
  }
  
  // Test environment mock store override
  if (process.env.NODE_ENV === 'test' && Object.prototype.hasOwnProperty.call(mockStore, key)) {
    return mockStore[key];
  }
  return undefined;
}

function initializeOpenFeature() {
  if (isInitialized || typeof window === 'undefined') return Promise.resolve();
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    const sdkKey = process.env.GATSBY_CONFIGCAT_SDK_KEY;

    if (!sdkKey) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[featureFlags] Missing GATSBY_CONFIGCAT_SDK_KEY, flags will return defaults.');
      }
      isInitialized = true;
      return;
    }

    try {
      // Poll interval configurable via env:
      // Fallbacks remain 10s (dev) / 120s (prod) if not set or invalid.
      const devInterval = parseInt(process.env.GATSBY_CONFIGCAT_POLL_INTERVAL_SECONDS_DEV || '', 10);
      const prodInterval = parseInt(process.env.GATSBY_CONFIGCAT_POLL_INTERVAL_SECONDS_PROD || '', 10);
      const pollIntervalSeconds =
        process.env.NODE_ENV === 'development'
          ? (Number.isFinite(devInterval) ? devInterval : 10)
          : (Number.isFinite(prodInterval) ? prodInterval : 120);

      // create ConfigCat provider for OpenFeature
      const configCatProvider = ConfigCatWebProvider.create(sdkKey, {
        pollIntervalSeconds,
        setupHooks: (hooks) => {
          hooks.on('configChanged', () => notifySubscribers());
        },
      });

      // Set the provider in OpenFeature (default provider)
      await OpenFeature.setProviderAndWait(configCatProvider);

      // Get the OpenFeature client
      openFeatureClient = OpenFeature.getClient();

      isInitialized = true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[featureFlags] Failed to initialize OpenFeature with ConfigCat:', e);
      isInitialized = true; // Mark as initialized to prevent retries
    }
  })();

  return _initPromise;
}

function initializeCommonOpenFeature() {
  if (isCommonInitialized || typeof window === 'undefined') return Promise.resolve();
  if (_commonInitPromise) return _commonInitPromise;

  _commonInitPromise = (async () => {
    const sdkKey = process.env.GATSBY_CONFIGCAT_COMMON_SDK_KEY;

    if (!sdkKey) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[featureFlags] Missing GATSBY_CONFIGCAT_COMMON_SDK_KEY, common flags will return defaults.');
      }
      isCommonInitialized = true;
      return;
    }

    try {
      // Poll interval configurable via env:
      const devInterval = parseInt(process.env.GATSBY_CONFIGCAT_POLL_INTERVAL_SECONDS_DEV || '', 10);
      const prodInterval = parseInt(process.env.GATSBY_CONFIGCAT_POLL_INTERVAL_SECONDS_PROD || '', 10);
      const pollIntervalSeconds =
        process.env.NODE_ENV === 'development'
          ? (Number.isFinite(devInterval) ? devInterval : 10)
          : (Number.isFinite(prodInterval) ? prodInterval : 120);

      // create ConfigCat provider for common flags
      const configCatCommonProvider = ConfigCatWebProvider.create(sdkKey, {
        pollIntervalSeconds,
        setupHooks: (hooks) => {
          hooks.on('configChanged', () => notifySubscribers());
        },
      });

      // Set the provider with a named client for common flags
      await OpenFeature.setProviderAndWait('common', configCatCommonProvider);

      // Get the named OpenFeature client for common flags
      openFeatureCommonClient = OpenFeature.getClient('common');

      isCommonInitialized = true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[featureFlags] Failed to initialize OpenFeature with ConfigCat Common:', e);
      isCommonInitialized = true; // Mark as initialized to prevent retries
    }
  })();

  return _commonInitPromise;
}

function getClient(clientType = 'default') {
  if (clientType === 'common') {
    if (!isCommonInitialized && typeof window !== 'undefined') {
      // Initialize on first call (fire-and-forget)
      initializeCommonOpenFeature();
    }
    return openFeatureCommonClient;
  }

  if (!isInitialized && typeof window !== 'undefined') {
    // Initialize on first call (fire-and-forget)
    initializeOpenFeature();
  }
  return openFeatureClient;
}

// Public API: async get
export async function getFlag(key, defaultValue, user, options = {}) {
  const { clientType = 'default' } = options;
  const override = getOverride(key);
  if (override !== undefined) return override;

  // Wait for initialization if not ready yet
  if (clientType === 'common') {
    if (!isCommonInitialized && typeof window !== 'undefined') {
      await initializeCommonOpenFeature();
    }
  } else {
    if (!isInitialized && typeof window !== 'undefined') {
      await initializeOpenFeature();
    }
  }

  const client = getClient(clientType);
  if (!client) return defaultValue;

  try {
    // Convert user object to OpenFeature context if provided.
    // Note: the web SDK ignores per-call context for flag evaluation; route context
    // (clientAlias/topicAlias) is set globally via setFlagRouteContext() instead.
    const context = user ? { targetingKey: user.identifier || 'anonymous', ...user } : undefined;

    // infer type from defaultValue and call relevant OpenFeature method
    if (typeof defaultValue === 'boolean') {
      return await client.getBooleanValue(key, defaultValue, context);
    }
    if (typeof defaultValue === 'string') {
      return await client.getStringValue(key, defaultValue, context);
    }
    if (typeof defaultValue === 'number') {
      return await client.getNumberValue(key, defaultValue, context);
    }
    // for objects, arrays, or other types
    return await client.getObjectValue(key, defaultValue, context);
  } catch (e) {
    return defaultValue;
  }
}

// React hook that returns the current flag value and re-renders on changes
export function useFlag(key, defaultValue, user, options = {}) {
  const { clientType = 'default' } = options;

  // Resolve any immediate override BEFORE initial render to avoid async flicker.
  const immediateOverride = getOverride(key);

  // memoize user object to avoid unnecessary re-evaluations
  const userRef = useMemo(() => user, [JSON.stringify(user || null)]);

  const [val, setVal] = useState(
    immediateOverride !== undefined ? immediateOverride : defaultValue
  );
  const mounted = useRef(true);

  useEffect(() => {
    // If we have an override, skip everything
    if (immediateOverride !== undefined) {
      return;
    }

    mounted.current = true;
    let active = true;

    async function evaluate() {
      // Re-check override in case tests inject after mount (rare but safe).
      const lateOverride = getOverride(key);
      if (lateOverride !== undefined) {
        if (mounted.current && active) setVal(lateOverride);
        return;
      }
      const v = await getFlag(key, defaultValue, userRef, { clientType });
      if (mounted.current && active) setVal(v);
    }

    evaluate();

    // Subscribe to flag changes
    const unsub = subscribe(() => {
      evaluate();
    });

    return () => {
      mounted.current = false;
      active = false;
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, immediateOverride, clientType]);
  // Return override value if it exists
  if (immediateOverride !== undefined) {
    return immediateOverride;
  }

  return val;
}

// Optional: expose a way to pre-set a value during development/testing
export function __setMockValue(key, value) {
  // Only for tests or local dev
  if (process.env.NODE_ENV === 'test') {
    mockStore[key] = value;
  }
}

const mockStore = {};
